/**
 * Coverage-workspace domain (CHC-869, ported & extended from prototype CHC-867).
 *
 * The unit of the workspace is the *whole routing surface*, not a single list
 * behind 6 filters. These pure functions classify each configuration, detect
 * missing ones ("gaps"), and roll the picture up into a health summary so the
 * landing screen can show "what is configured / empty / incomplete / conflict /
 * disabled" at a glance and surface gaps BEFORE they reach production.
 *
 * Status is derived from the adapter rows + their amount coverage, reusing the
 * same routing primitives the editor and simulation use, so the map and the
 * detail can never disagree.
 *
 * CHC-869 extensions over the prototype:
 *  - DISABLED status — an intentionally-off configuration (entry.enabled === false).
 *    Decoupled from EMPTY (a forgotten gap). DISABLED is NOT a gap risk and is
 *    sorted to the bottom of the map.
 *  - `order` — optional manual sort order for the map list (drag-reorder).
 */

import type { AdapterRow, RoutingContext } from './types'
import { buildCoverage } from './routing'
import { encodeContextId } from './context'

export type ConfigStatus = 'CONFIGURED' | 'EMPTY' | 'INCOMPLETE' | 'CONFLICT' | 'DISABLED'

/** Amount-axis coverage summary for one configuration. */
export interface CoverageSummary {
  minCovered: number | null
  maxCovered: number | null
  hasHoles: boolean
  hasOverlaps: boolean
}

/** Dimension reference data — the full space the operator can configure. */
export interface CoverageDimensions {
  merchants: Array<{ token: string; title: string }>
  paymentMethods: string[]
  currencies: string[]
  directions: ('PAYMENT' | 'PAYOUT')[]
  externalUserFlags: string[]
  sources: ('MERCHANT' | 'PLATFORM')[]
}

/** One row of the coverage map as returned by the coverage endpoint. */
export interface CoverageEntry {
  context: RoutingContext
  status: ConfigStatus
  enabledAdapterCount: number
  totalAdapterCount: number
  coverage: CoverageSummary
  updatedAt: string
  /**
   * CHC-869: whether the whole configuration is intentionally on. When false
   * the status is reported as DISABLED regardless of its adapter coverage.
   * Backend proposal: persist as a per-configuration `enabled` flag.
   */
  enabled: boolean
  /**
   * CHC-869: manual sort order on the map (lower = higher). Optional; absent
   * entries fall back to updatedAt ordering. Backend proposal: persist `order`.
   */
  order?: number
}

export interface CoverageResponse {
  dimensions: CoverageDimensions
  configurations: CoverageEntry[]
  expectedContexts: RoutingContext[]
}

/** A detected gap: an expected context with no active configuration. */
export interface Gap {
  context: RoutingContext
  contextId: string
}

export interface HealthSummary {
  configured: number
  incomplete: number
  conflict: number
  disabled: number
  /**
   * CHC-874: «Требуют настройки» — единый риск-счётчик. «Пусто» убран как
   * отдельный статус: сюда входят и отсутствующие конфиги (ожидаемый контекст
   * без строки), и конфиги без рабочего адаптера (бывш. EMPTY). Оба = «контекст
   * должен маршрутизировать, но не маршрутизирует непреднамеренно». DISABLED
   * (намеренно выключено) сюда НЕ входит.
   */
  gaps: number
  total: number
}

/**
 * Summarise amount coverage from the working adapter rows. Considers only
 * enabled + globally-enabled adapters with a valid range (same predicate the
 * router uses).
 */
export function summarizeCoverage(rows: AdapterRow[]): CoverageSummary {
  const segments = buildCoverage(rows)
  const covered = segments.filter((s) => s.kind !== 'hole')
  if (covered.length === 0) {
    return { minCovered: null, maxCovered: null, hasHoles: true, hasOverlaps: false }
  }

  const minCovered = Math.min(...covered.map((s) => s.from))
  const maxFinite = covered.map((s) => s.to).filter((to): to is number => to !== null)
  const openEnded = covered.some((s) => s.to === null)
  const maxCovered = openEnded ? null : maxFinite.length ? Math.max(...maxFinite) : null

  // A hole is meaningful only if it sits BETWEEN covered spans (an interior
  // gap). The leading hole [0..min-1] and the trailing hole above the top are
  // expected and not treated as coverage defects.
  const hasInteriorHole = segments.some(
    (s, i) => s.kind === 'hole' && i > 0 && i < segments.length - 1
  )

  return {
    minCovered,
    maxCovered,
    hasHoles: hasInteriorHole,
    hasOverlaps: segments.some((s) => s.kind === 'overlap'),
  }
}

/**
 * Classify a single configuration from its adapter rows.
 *
 *  - EMPTY      — no rows, or no adapter is enabled (clients would get errors).
 *  - CONFLICT   — an enabled adapter has an invalid range (min > max).
 *  - INCOMPLETE — enabled adapters exist but coverage has interior holes.
 *  - CONFIGURED — enabled adapters with hole-free coverage. Overlaps allowed.
 *
 * The DISABLED status is NOT derived here — it is a configuration-level flag
 * (entry.enabled === false). Use {@link statusOfEntry} when an `enabled` flag
 * is known so an intentionally-off config is not mistaken for an EMPTY gap.
 */
export function computeConfigStatus(rows: AdapterRow[]): ConfigStatus {
  const enabled = rows.filter((r) => r.enabled && r.globallyEnabled)
  if (enabled.length === 0) return 'EMPTY'

  const hasInvalidRange = enabled.some((r) => r.minDeposit > r.maxDeposit)
  if (hasInvalidRange) return 'CONFLICT'

  const { hasHoles } = summarizeCoverage(rows)
  if (hasHoles) return 'INCOMPLETE'

  return 'CONFIGURED'
}

/**
 * Resolve the displayed status of a configuration given its derived adapter
 * status and its configuration-level `enabled` flag. A disabled configuration
 * is reported DISABLED — intentional off, not a forgotten gap.
 */
export function statusOfEntry(derived: ConfigStatus, enabled: boolean): ConfigStatus {
  return enabled ? derived : 'DISABLED'
}

/**
 * Detect gaps: expected contexts that have NO configuration row at all (CHC-874).
 * These are the "missing" cases shown in the «Требуют настройки» block (offer to
 * fill from template). A context that HAS a row — even an EMPTY one (no working
 * adapter) — is not a gap here: it surfaces in the list as a «Требует настройки»
 * row and is folded into the needs-setup counter by {@link summarizeHealth}.
 * DISABLED rows are intentional and never a gap.
 */
export function findGaps(
  expectedContexts: RoutingContext[],
  configurations: CoverageEntry[]
): Gap[] {
  const existingIds = new Set(configurations.map((c) => encodeContextId(c.context)))

  return expectedContexts
    .filter((ctx) => !existingIds.has(encodeContextId(ctx)))
    .map((ctx) => ({ context: ctx, contextId: encodeContextId(ctx) }))
}

/**
 * Roll the coverage map up into the health counters shown at the top of the
 * landing screen.
 */
export function summarizeHealth(
  configurations: CoverageEntry[],
  gaps: Gap[]
): HealthSummary {
  const counts = { configured: 0, incomplete: 0, conflict: 0, disabled: 0 }
  let emptyConfigs = 0
  for (const c of configurations) {
    switch (c.status) {
      case 'CONFIGURED':
        counts.configured++
        break
      // CHC-874: EMPTY (конфиг без рабочего адаптера) — не отдельный счётчик,
      // а часть «Требуют настройки» вместе с отсутствующими конфигами.
      case 'EMPTY':
        emptyConfigs++
        break
      case 'INCOMPLETE':
        counts.incomplete++
        break
      case 'CONFLICT':
        counts.conflict++
        break
      case 'DISABLED':
        counts.disabled++
        break
    }
  }
  return {
    ...counts,
    gaps: gaps.length + emptyConfigs,
    total: configurations.length,
  }
}

/**
 * Sort configurations for the map list (CHC-869 UX fix #3):
 *  - DISABLED configs are forced to the bottom regardless of manual order.
 *  - within each band, manual `order` wins (lower first); ties / missing order
 *    fall back to most-recently-updated first.
 */
export function sortConfigurations(configurations: CoverageEntry[]): CoverageEntry[] {
  return [...configurations].sort((a, b) => {
    const aDisabled = a.status === 'DISABLED'
    const bDisabled = b.status === 'DISABLED'
    if (aDisabled !== bDisabled) return aDisabled ? 1 : -1

    const ao = a.order ?? Number.POSITIVE_INFINITY
    const bo = b.order ?? Number.POSITIVE_INFINITY
    if (ao !== bo) return ao - bo

    // Fallback: newest first.
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })
}
